import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import dayjs from "dayjs";
import { api, type ApiAccount } from "../../services/api";

type TransferDialogProps = {
  open: boolean;
  onClose: () => void;
  accounts: ApiAccount[];
  onSuccess?: () => void;
};

export default function TransferDialog({
  open,
  onClose,
  accounts,
  onSuccess,
}: TransferDialogProps) {
  const [fromAccountId, setFromAccountId] = useState("");
  const [toAccountId, setToAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setFromAccountId("");
    setToAccountId("");
    setAmount("");
    setDate(dayjs().format("YYYY-MM-DD"));
    setDescription("");
    setNotes("");
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (!fromAccountId || !toAccountId) {
      setError("Please select both accounts.");
      return;
    }
    if (fromAccountId === toAccountId) {
      setError("Source and destination accounts must be different.");
      return;
    }
    if (isNaN(amountCents) || amountCents <= 0) {
      setError("Amount must be a positive number.");
      return;
    }

    setLoading(true);
    try {
      await api.createTransfer({
        fromAccountId,
        toAccountId,
        amountCents,
        occurredAt: new Date(date).toISOString(),
        description: description || "Transfer",
        notes: notes || undefined,
      });
      handleClose();
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Transfer failed");
    } finally {
      setLoading(false);
    }
  };

  const activeAccounts = accounts.filter((a) => a.status === "active");

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SwapHorizIcon color="primary" />
          <span>Transfer Between Accounts</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth>
            <InputLabel>From Account</InputLabel>
            <Select
              value={fromAccountId}
              label="From Account"
              onChange={(e) => setFromAccountId(e.target.value)}
            >
              {activeAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>To Account</InputLabel>
            <Select
              value={toAccountId}
              label="To Account"
              onChange={(e) => setToAccountId(e.target.value)}
            >
              {activeAccounts
                .filter((a) => a.id !== fromAccountId)
                .map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            label="Amount"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: "0.01" }}
          />

          <TextField
            label="Date"
            type="date"
            fullWidth
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Description"
            fullWidth
            placeholder="Transfer"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <TextField
            label="Notes"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SwapHorizIcon />}
        >
          Transfer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
